import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * Displays a summary of blocked user attributes aggregated from all blocked events
 * @param {Array} validationResults - Array of beacon objects from validation
 */
function BlockedAttributesSummary({ validationResults }) {
  // Aggregate unique blocked user attributes from all events
  const uniqueBlockedAttributes = React.useMemo(() => {
    const attributeSet = new Set();

    validationResults.forEach((beacon) => {
      if (beacon.blockedUserAttributes && Array.isArray(beacon.blockedUserAttributes)) {
        beacon.blockedUserAttributes.forEach((attr) => {
          attributeSet.add(attr);
        });
      }
    });

    return Array.from(attributeSet).sort();
  }, [validationResults]);

  // Don't render if no blocked attributes found
  if (uniqueBlockedAttributes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.16),
        border: (theme) => `1px solid ${theme.palette.warning.light}`,
        borderRadius: '4px',
        padding: '12px 16px',
        marginBottom: '16px',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: 'warning.dark',
          marginBottom: '8px',
        }}
      >
        Blocked User Attributes
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        {uniqueBlockedAttributes.map((attr, index) => (
          <Box
            key={index}
            sx={{
              backgroundColor: 'background.paper',
              border: (theme) => `1px solid ${theme.palette.warning.light}`,
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '0.875rem',
              color: 'warning.dark',
              fontFamily: 'monospace',
            }}
          >
            {attr}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default BlockedAttributesSummary;
